import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
    Mail, Send, Eye, CheckCircle2, MailX, AlertTriangle,
    Building2, ChevronDown, ChevronRight, Plus, Search, RefreshCw, X,
} from 'lucide-react';
import api from '../lib/api';
import { campaignsAPI } from '../lib/api';
import CampaignBuilder from '../components/campaigns/CampaignBuilder';
import CampaignsTable from '../components/campaigns/CampaignsTable';
import CampaignDrawer from '../components/campaigns/CampaignDrawer';
import AnalyticsTab from '../components/analytics/AnalyticsTab';
import SchedulerTab from '../components/communications/SchedulerTab';
import { useAuth } from '../lib/auth';
// P_USER_WELCOME_DISPATCH_2.1 — shared timezone helpers. The
// helpers were originally defined inline here; extracted to
// `utils/localTime.js` so other pages exhibiting the same naive
// `slice(...)` UTC bug can reuse them.
import { fmtLocalTime, fmtLocalDateTime } from '../utils/localTime';

/* ──────────────────────────────────────────────────────────────────
 * Historical context (see utils/localTime.js for full background):
 * earlier versions of this screen extracted display strings via
 * substring slicing of UTC ISO timestamps, rendering them as if
 * they were already in the operator's local time. Production bug
 * 2026-06-29: dispatches opened at noon EDT showed as "4:00 PM" —
 * a fake "future" timestamp.
 * ────────────────────────────────────────────────────────────────── */

/**
 * P_USER_WELCOME_DISPATCH_1.1 — Communications Hub polish pass.
 * Org accordion + enriched summary cards + filter bar + redesigned
 * drawer. NO regeneration — every body shown is the persisted
 * immutable string from db.daily_welcome_dispatches.
 *
 * Deep link: ?dispatch_id=<id> auto-opens the drawer (used by Audit
 * Log "View what user saw at 08:02 AM").
 */

const TABS = [
    { id: 'all',           key: 'welcomeHub.tabs.all'           },
    { id: 'daily',         key: 'welcomeHub.tabs.daily'         },
    { id: 'campaigns',     key: 'welcomeHub.tabs.campaigns'     },
    { id: 'scheduled',     key: 'welcomeHub.tabs.scheduled'     },
    { id: 'history',       key: 'welcomeHub.tabs.history'       },
    { id: 'analytics',     key: 'welcomeHub.tabs.analytics'     },
    { id: 'scheduler',     key: 'welcomeHub.tabs.scheduler',   superAdminOnly: true },
    { id: 'help',          key: 'welcomeHub.tabs.help'          },
];

const StatusIcon = ({ status, when, t }) => {
    if (status === 'ok' || when) {
        return (
            <span className="inline-flex items-center gap-1.5 text-sm" data-testid="status-ok">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-foreground">{when || t('welcomeHub.status.ok')}</span>
            </span>
        );
    }
    if (status === 'failed') {
        return (
            <span className="inline-flex items-center gap-1.5 text-sm" data-testid="status-failed">
                <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                <span className="text-rose-600 dark:text-rose-400">{t('welcomeHub.status.failed')}</span>
            </span>
        );
    }
    return <span className="text-muted-foreground text-sm">—</span>;
};

const StatCard = ({ icon: Icon, iconBg, label, value, hint, hintTone = 'muted', testid }) => {
    const toneClass = hintTone === 'positive'
        ? 'text-emerald-600 dark:text-emerald-400'
        : hintTone === 'negative'
            ? 'text-rose-600 dark:text-rose-400'
            : 'text-muted-foreground';
    return (
        <Card className="p-5 flex items-center gap-4" data-testid={testid}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-3xl font-semibold text-foreground leading-tight">{value}</div>
                {hint ? <div className={`text-xs ${toneClass} mt-0.5`}>{hint}</div> : null}
            </div>
        </Card>
    );
};

const todayUtc = () => new Date().toISOString().slice(0, 10);

const UserWelcomeDispatch = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || undefined;
    const auth = useAuth();
    const isSuperAdmin = !!auth?.isSuperAdmin?.();
    const visibleTabs = TABS.filter(tb => !tb.superAdminOnly || isSuperAdmin);
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState('all');
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(params.get('date') || todayUtc());
    const [search, setSearch] = useState('');
    const [orgFilter, setOrgFilter] = useState(params.get('organization_id') || '');
    const [statusFilter, setStatusFilter] = useState('');
    const [collapsed, setCollapsed] = useState({});
    const [selectedId, setSelectedId] = useState(
        // P_USER_WELCOME_DISPATCH_3.1 — when campaign_id is also in the
        // URL, the dispatch_id belongs to the campaign drawer; don't
        // also fire the welcome drawer.
        params.get('campaign_id') ? null : (params.get('dispatch_id') || null)
    );
    const [showFullMessage, setShowFullMessage] = useState(false);
    // P_USER_WELCOME_DISPATCH_3.0a — Campaign Builder modal state.
    const [builderOpen, setBuilderOpen] = useState(false);
    const [campaignsRefreshKey, setCampaignsRefreshKey] = useState(0);
    // P_USER_WELCOME_DISPATCH_3.0b — Drawer + edit + audit deep-link state.
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [drawerCampaignId, setDrawerCampaignId] = useState(params.get('campaign_id') || null);
    const [drawerDispatchId, setDrawerDispatchId] = useState(params.get('dispatch_id') || null);

    // Deep-link: when ?campaign_id=X is present, switch to the Campaigns
    // tab on first mount so the surrounding context is visible.
    // P_WELCOME_HUB_SCHEDULER_TAB_1.0: ?tab=scheduler honours bookmarks
    // and the /admin/scheduler legacy redirect.
    useEffect(() => {
        const tabParam = params.get('tab');
        if (tabParam && visibleTabs.find(tb => tb.id === tabParam)) {
            setTab(tabParam);
        } else if (params.get('campaign_id') && tab === 'all') {
            setTab('campaigns');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep drawer state in sync with URL for shareability.
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (drawerCampaignId) next.set('campaign_id', drawerCampaignId); else next.delete('campaign_id');
        if (drawerDispatchId) next.set('dispatch_id', drawerDispatchId); else next.delete('dispatch_id');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerCampaignId, drawerDispatchId]);

    const openCampaignDrawer = (campaignId, dispatchId = null) => {
        setDrawerCampaignId(campaignId);
        setDrawerDispatchId(dispatchId);
    };
    const closeCampaignDrawer = () => {
        setDrawerCampaignId(null);
        setDrawerDispatchId(null);
    };
    const openEditDraft = async (campaign) => {
        try {
            const r = await campaignsAPI.get(campaign.id);
            setEditingCampaign(r.data);
            setBuilderOpen(true);
        } catch (_e) { /* surfaces via toast/no-op */ }
    };

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            // Pass the picked date to BOTH endpoints so summary
            // totals and table row count agree row-for-row.
            const reqs = [api.get('/admin/user-welcome-dispatch/summary', { params: { date } }),
                          api.get('/admin/user-welcome-dispatch/dispatches', { params: { date } })];
            const [s, d] = await Promise.all(reqs);
            setSummary(s.data);
            setRows(d.data?.items || []);
        } catch (_e) { /* surfaces in empty state */ }
        finally { setLoading(false); }
    }, [date]);
    useEffect(() => { reload(); }, [reload]);

    // Keep URL in sync for deep-linking (e.g. Audit Log entry points).
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (selectedId) next.set('dispatch_id', selectedId); else next.delete('dispatch_id');
        if (date && date !== todayUtc()) next.set('date', date); else next.delete('date');
        setParams(next, { replace: true });
    }, [selectedId, date]);

    // Filter + group by organization.
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r => {
            if (orgFilter && r.organization_id !== orgFilter) return false;
            if (statusFilter && r.status !== statusFilter) return false;
            if (tab === 'daily' || tab === 'all') {
                /* both show Daily Welcome rows for now */
            }
            if (q) {
                const hay = `${r.user_name} ${r.user_email} ${r.organization_name}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [rows, orgFilter, statusFilter, search, tab]);

    const orgGroups = useMemo(() => {
        const map = {};
        for (const r of filteredRows) {
            const key = r.organization_id || 'none';
            if (!map[key]) map[key] = { id: key, name: r.organization_name || '—', rows: [] };
            map[key].rows.push(r);
        }
        return Object.values(map).sort((a, b) => b.rows.length - a.rows.length);
    }, [filteredRows]);

    const orgs = useMemo(() => {
        const seen = new Map();
        for (const r of rows) {
            if (r.organization_id && !seen.has(r.organization_id)) {
                seen.set(r.organization_id, r.organization_name);
            }
        }
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }, [rows]);

    // Derived metrics for the secondary lines on each summary card.
    const usersTotal = summary?.users_total || 0;
    const genCount = summary?.generated_today || 0;
    const seenCount = summary?.seen_today ?? null;
    const ackCount = summary?.acknowledged_today ?? null;
    const unopenedCount = summary?.unopened_today ?? summary?.pending_today ?? 0;
    const failed = summary?.failures_today || 0;
    const openRate = genCount > 0 ? Math.round(((seenCount ?? 0) / genCount) * 100) : 0;
    const ackRate = genCount > 0 ? Math.round(((ackCount ?? 0) / genCount) * 100) : 0;
    const failRate = genCount > 0 ? Math.round((failed / Math.max(genCount, 1)) * 1000) / 10 : 0;
    const coverage = usersTotal > 0 ? Math.round((genCount / usersTotal) * 100) : 0;

    return (
        <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6" data-testid="user-welcome-dispatch-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-foreground" data-testid="welcome-hub-title">
                        {t('welcomeHub.title')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t('welcomeHub.subtitleLong')}</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="welcome-hub-reload">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        {t('welcomeHub.reload')}
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setBuilderOpen(true)}
                        className="bg-primary text-primary-foreground"
                        data-testid="welcome-hub-create">
                        <Plus className="w-4 h-4 mr-1" />
                        {t('welcomeHub.create')}
                    </Button>
                </div>
            </div>

            {/* Summary stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatCard icon={Send} iconBg="bg-blue-500"
                    label={t('welcomeHub.summary.sentToday')}
                    value={genCount}
                    hint={`${coverage}% ${t('welcomeHub.summary.coverage')}`}
                    hintTone={coverage >= 80 ? 'positive' : 'muted'}
                    testid="card-sent-today" />
                <StatCard icon={Eye} iconBg="bg-emerald-500"
                    label={t('welcomeHub.summary.seenToday')}
                    value={seenCount ?? '—'}
                    hint={`${openRate}% ${t('welcomeHub.summary.openRate')}`}
                    hintTone="positive"
                    testid="card-seen-today" />
                <StatCard icon={CheckCircle2} iconBg="bg-amber-500"
                    label={t('welcomeHub.summary.ackToday')}
                    value={ackCount ?? '—'}
                    hint={`${ackRate}% ${t('welcomeHub.summary.ackRate')}`}
                    hintTone={ackRate >= 70 ? 'positive' : 'muted'}
                    testid="card-ack-today" />
                <StatCard icon={MailX} iconBg="bg-rose-500"
                    label={t('welcomeHub.summary.unopened')}
                    value={unopenedCount}
                    hint={genCount > 0 ? `${Math.round((unopenedCount / genCount) * 100)}% ${t('welcomeHub.summary.notSeen')}` : t('welcomeHub.summary.notSeen')}
                    hintTone={unopenedCount > 0 ? 'negative' : 'muted'}
                    testid="card-unopened" />
                <StatCard icon={AlertTriangle} iconBg="bg-purple-500"
                    label={t('welcomeHub.summary.failures')}
                    value={failed}
                    hint={`${failRate}% ${t('welcomeHub.summary.failureRate')}`}
                    hintTone={failed > 0 ? 'negative' : 'muted'}
                    testid="card-failures" />
            </div>

            {/* Tabs */}
            <Card className="p-4 md:p-5 space-y-4" data-testid="dispatch-panel">
                <div className="flex gap-1 overflow-x-auto border-b border-border" data-testid="tabs">
                    {visibleTabs.map(tabSpec => (
                        <button key={tabSpec.id}
                            onClick={() => setTab(tabSpec.id)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                                ${tab === tabSpec.id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                            data-testid={`tab-${tabSpec.id}`}>
                            {t(tabSpec.key)}
                        </button>
                    ))}
                </div>

                {/* Tab body */}
                {tab === 'campaigns' ? (
                    <div data-testid="campaigns-tab-body">
                        <CampaignsTable
                            refreshKey={campaignsRefreshKey}
                            onOpenDrawer={(cid) => openCampaignDrawer(cid)}
                            onEditDraft={(c) => openEditDraft(c)} />
                    </div>
                ) : tab === 'scheduled' ? (
                    <div data-testid="scheduled-tab-body">
                        <CampaignsTable
                            refreshKey={campaignsRefreshKey}
                            statusFilter="scheduled"
                            onOpenDrawer={(cid) => openCampaignDrawer(cid)}
                            onEditDraft={(c) => openEditDraft(c)} />
                    </div>
                ) : tab === 'history' ? (
                    <div data-testid="history-tab-body">
                        <CampaignsTable
                            refreshKey={campaignsRefreshKey}
                            statusFilter="completed,cancelled"
                            onOpenDrawer={(cid) => openCampaignDrawer(cid)}
                            onEditDraft={null} />
                    </div>
                ) : tab === 'analytics' ? (
                    <div data-testid="analytics-tab-body">
                        <AnalyticsTab />
                    </div>
                ) : tab === 'scheduler' ? (
                    <div data-testid="scheduler-tab-wrapper">
                        <SchedulerTab isSuperAdmin={isSuperAdmin} />
                    </div>
                ) : tab === 'help' ? (
                    <div data-testid="help-tab-body" className="p-4 space-y-3 text-sm text-foreground">
                        <div className="font-medium">{t('welcomeHub.help.title')}</div>
                        <p className="text-muted-foreground">{t('welcomeHub.help.intro')}</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                            <li>{t('welcomeHub.help.immutability')}</li>
                            <li>{t('welcomeHub.help.translation')}</li>
                            <li>{t('welcomeHub.help.scheduler')}</li>
                            <li>{t('welcomeHub.help.audit')}</li>
                        </ul>
                    </div>
                ) : (
                <>
                {/* Filter row */}
                <div className="flex flex-col md:flex-row gap-2 md:items-center" data-testid="filter-row">
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                        className="md:w-44" data-testid="filter-date" />
                    <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}
                        className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground md:w-56"
                        data-testid="filter-org">
                        <option value="">{t('welcomeHub.filters.allOrgs')}</option>
                        {orgs.map(o => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                    </select>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground md:w-40"
                        data-testid="filter-status">
                        <option value="">{t('welcomeHub.filters.allStatuses')}</option>
                        <option value="ok">{t('welcomeHub.status.ok')}</option>
                        <option value="failed">{t('welcomeHub.status.failed')}</option>
                    </select>
                    <div className="relative md:flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('welcomeHub.filters.searchUsers')}
                            className="pl-9" data-testid="filter-search" />
                    </div>
                </div>

                {/* Org accordion */}
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground" data-testid="daily-loading">{t('welcomeHub.loading')}</div>
                ) : orgGroups.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground" data-testid="daily-empty">{t('welcomeHub.empty')}</div>
                ) : (
                    <div className="space-y-2" data-testid="org-groups">
                        {orgGroups.map(group => {
                            const isCollapsed = !!collapsed[group.id];
                            return (
                                <div key={group.id} className="border border-border rounded-lg overflow-hidden" data-testid={`org-group-${group.id}`}>
                                    <button onClick={() => setCollapsed({ ...collapsed, [group.id]: !isCollapsed })}
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors"
                                        data-testid={`org-toggle-${group.id}`}>
                                        <div className="flex items-center gap-2">
                                            {isCollapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                                            <Building2 className="w-4 h-4 text-blue-500" />
                                            <span className="font-medium text-foreground">{group.name}</span>
                                        </div>
                                        <span className="text-xs text-muted-foreground">{group.rows.length} {t('welcomeHub.usersCount')}</span>
                                    </button>
                                    {!isCollapsed ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-background border-b border-border">
                                                    <tr className="text-xs uppercase text-muted-foreground">
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.user')}</th>
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.generated')}</th>
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.delivered')}</th>
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.seen')}</th>
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.views')}</th>
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.ack')}</th>
                                                        <th className="text-left px-4 py-2 font-medium">{t('welcomeHub.cols.lang')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.rows.map(r => {
                                                        const langDisplay = (r.language_delivered || r.language_at_generation || r.user_language || 'en').toUpperCase();
                                                        // P_USER_WELCOME_DISPATCH_2.1 — render UTC ISO timestamps
                                                        // in the viewer's local timezone (helper at module top).
                                                        const genTime = fmtLocalTime(r.generated_at, lang) || '—';
                                                        const delTime = fmtLocalTime(r.delivered_at, lang) || genTime;
                                                        const seenTime = fmtLocalTime(r.first_seen_at, lang);
                                                        const views = r.view_count ?? 0;
                                                        const acked = !!r.acknowledged_at;
                                                        return (
                                                        <tr key={r.id}
                                                            onClick={() => setSelectedId(r.id)}
                                                            className={`border-b border-border last:border-b-0 hover:bg-muted/40 cursor-pointer transition-colors
                                                                ${selectedId === r.id ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
                                                            data-testid={`dispatch-row-${r.id}`}>
                                                            <td className="px-4 py-3">
                                                                <div className="font-medium text-foreground">{r.user_name}</div>
                                                                <div className="text-xs text-muted-foreground">{r.user_email}</div>
                                                            </td>
                                                            <td className="px-4 py-3 text-foreground" data-testid={`cell-generated-${r.id}`}>{genTime}</td>
                                                            <td className="px-4 py-3" data-testid={`cell-delivered-${r.id}`}>
                                                                {r.status === 'failed'
                                                                    ? <span className="inline-flex items-center gap-1.5 text-rose-600 dark:text-rose-400"><AlertTriangle className="w-4 h-4" />{t('welcomeHub.status.failed')}</span>
                                                                    : <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" />{delTime}</span>}
                                                            </td>
                                                            <td className="px-4 py-3" data-testid={`cell-seen-${r.id}`}>
                                                                {seenTime
                                                                    ? <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><Eye className="w-4 h-4" />{seenTime}</span>
                                                                    : <span className="text-muted-foreground">—</span>}
                                                            </td>
                                                            <td className="px-4 py-3 tabular-nums text-foreground" data-testid={`cell-views-${r.id}`}>{views}</td>
                                                            <td className="px-4 py-3" data-testid={`cell-ack-${r.id}`}>
                                                                {acked
                                                                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                                                    : <span className="text-muted-foreground">—</span>}
                                                            </td>
                                                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground" data-testid={`cell-lang-${r.id}`}>{langDisplay}</td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                )}
                </>
                )}
            </Card>

            {/* P_USER_WELCOME_DISPATCH_3.0a — Campaign Builder modal */}
            <CampaignBuilder
                open={builderOpen}
                editingCampaign={editingCampaign}
                onClose={() => { setBuilderOpen(false); setEditingCampaign(null); }}
                onCreated={(cid, info) => {
                    setCampaignsRefreshKey(k => k + 1);
                    if (info?.kind === 'scheduled') setTab('scheduled');
                    else setTab('campaigns');
                }} />

            {/* P_USER_WELCOME_DISPATCH_3.0b — Read-only campaign drawer */}
            <CampaignDrawer
                campaignId={drawerCampaignId}
                dispatchId={drawerDispatchId}
                onClose={closeCampaignDrawer} />

            {/* Drawer */}
            <Drawer
                dispatchId={selectedId}
                onClose={() => { setSelectedId(null); setShowFullMessage(false); }}
                showFull={showFullMessage}
                setShowFull={setShowFullMessage}
                t={t}
            />
        </div>
    );
};

// ─── Drawer ──────────────────────────────────────────────────────────

const Drawer = ({ dispatchId, onClose, showFull, setShowFull, t }) => {
    // P_USER_WELCOME_DISPATCH_2.1 — get the live i18n locale so
    // fmtLocalDateTime renders dates in the operator's language.
    const { i18n } = useTranslation();
    const lang = i18n.language || undefined;
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!dispatchId) { setDoc(null); return; }
        setLoading(true);
        api.get(`/admin/user-welcome-dispatch/dispatches/${dispatchId}`)
            .then(r => setDoc(r.data)).catch(() => setDoc(null))
            .finally(() => setLoading(false));
    }, [dispatchId]);

    const userLang = doc?.user?.preferred_language || doc?.language_at_generation || 'en';
    const previewBody = doc?.[`body_${userLang}`] || doc?.body_en;

    return (
        <Sheet open={!!dispatchId} onOpenChange={(o) => !o && onClose()}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto bg-background"
                data-testid="dispatch-drawer">
                <SheetHeader className="space-y-1">
                    <div className="flex items-center justify-between">
                        <SheetTitle data-testid="drawer-title">{t('welcomeHub.drawer.title')}</SheetTitle>
                        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="drawer-close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </SheetHeader>

                {loading ? <div className="text-muted-foreground mt-4" data-testid="drawer-loading">{t('welcomeHub.loading')}</div> : null}

                {doc ? (
                    <div className="mt-4 space-y-5">
                        {/* User header */}
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold">
                                {(doc.user?.name || doc.user?.email || '?')[0]?.toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-foreground" data-testid="drawer-user-name">{doc.user?.name || doc.user?.email || '—'}</div>
                                <div className="text-xs text-muted-foreground">{doc.user?.role || '—'}</div>
                                <div className="text-xs text-muted-foreground">{doc.organization?.name || '—'}</div>
                            </div>
                        </div>

                        {/* Message preview (user's preferred language) */}
                        <Card className="p-4 bg-blue-50/60 dark:bg-blue-950/20 border-blue-100 dark:border-blue-900/40" data-testid="drawer-message-preview">
                            <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                                {t('welcomeHub.drawer.messagePreview')}
                            </div>
                            <pre className="whitespace-pre-wrap text-sm text-foreground font-sans">{previewBody || '—'}</pre>
                            <Button variant="outline" size="sm" className="mt-3 w-full"
                                onClick={() => setShowFull(v => !v)}
                                data-testid="drawer-view-full">
                                {showFull ? t('welcomeHub.drawer.hideFull') : t('welcomeHub.drawer.viewFull')}
                            </Button>
                        </Card>

                        {/* All three persisted language bodies (immutable record) */}
                        {showFull ? (
                            <div className="space-y-3" data-testid="drawer-full-bodies">
                                {['en', 'fr', 'es'].map(lang => (
                                    <Card key={lang} className="p-3 bg-muted/30" data-testid={`drawer-body-${lang}`}>
                                        <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                                            {t(`welcomeHub.drawer.lang.${lang}`)}
                                        </div>
                                        <pre className="whitespace-pre-wrap text-xs text-foreground font-sans">
                                            {doc[`body_${lang}`] || '—'}
                                        </pre>
                                    </Card>
                                ))}
                            </div>
                        ) : null}

                        {/* Delivery & engagement */}
                        <div>
                            <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                                {t('welcomeHub.drawer.deliverySection')}
                            </div>
                            <dl className="text-sm space-y-1.5">
                                <Row label={t('welcomeHub.drawer.generated')} value={fmtLocalDateTime(doc.generated_at, lang) || '—'} testid="drawer-generated-at" />
                                <Row label={t('welcomeHub.drawer.source')} value={doc.source} testid="drawer-source" />
                                <Row label={t('welcomeHub.drawer.timezone')} value={doc.timezone} testid="drawer-timezone" />
                                <Row label={t('welcomeHub.drawer.date')} value={doc.date} testid="drawer-date" />
                                <Row label={t('welcomeHub.drawer.status')}
                                    value={<Badge className={doc.status === 'ok'
                                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800'
                                        : 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800'}>
                                        {doc.status === 'ok' ? t('welcomeHub.status.ok') : t('welcomeHub.status.failed')}
                                    </Badge>} />
                            </dl>
                        </div>

                        {/* Engagement — Phase 2.0 */}
                        <div data-testid="drawer-engagement">
                            <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                                {t('welcomeHub.drawer.engagementSection')}
                            </div>
                            <dl className="text-sm space-y-1.5">
                                <Row label={t('welcomeHub.drawer.firstSeen')} value={fmtLocalDateTime(doc.first_seen_at, lang) || '—'} testid="drawer-first-seen" />
                                <Row label={t('welcomeHub.drawer.lastSeen')} value={fmtLocalDateTime(doc.last_seen_at, lang) || '—'} testid="drawer-last-seen" />
                                <Row label={t('welcomeHub.drawer.viewCount')} value={doc.view_count ?? 0} testid="drawer-view-count" />
                                <Row label={t('welcomeHub.drawer.acknowledgedAt')} value={fmtLocalDateTime(doc.acknowledged_at, lang) || '—'} testid="drawer-ack-at" />
                            </dl>
                        </div>

                        {/* Technical info */}
                        <div>
                            <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                                {t('welcomeHub.drawer.techSection')}
                            </div>
                            <dl className="text-sm space-y-1.5">
                                <Row label={t('welcomeHub.drawer.engine')} value={`v${doc.engine_version}`} />
                                <Row label={t('welcomeHub.drawer.pricingSource')} value={doc.pricing_source} />
                                <Row label={t('welcomeHub.drawer.language')} value={(doc.user?.preferred_language || doc.language_at_generation || 'en').toUpperCase()} />
                            </dl>
                        </div>

                        {doc.failure_reason ? (
                            <Card className="p-3 bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs" data-testid="drawer-failure">
                                {doc.failure_reason}
                            </Card>
                        ) : null}

                        <SheetDescription className="text-xs">
                            {t('welcomeHub.drawer.immutabilityNote')}
                        </SheetDescription>
                    </div>
                ) : null}
            </SheetContent>
        </Sheet>
    );
};

const Row = ({ label, value, testid }) => (
    <div className="flex items-center justify-between gap-3" data-testid={testid}>
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="text-foreground text-right truncate">{value || '—'}</dd>
    </div>
);

export default UserWelcomeDispatch;
